/**
 * runtimeConfig.ts
 *
 * Loads /config/config.json at app startup and caches it.
 * This allows one generic Docker image to be deployed to any environment —
 * environment-specific values are injected by configure.sh at deploy time
 * (after terraform apply), matching the caduceus pattern.
 *
 * Usage:
 *   // In index.tsx — before rendering:
 *   loadConfig().then(() => ReactDOM.createRoot(...).render(...))
 *
 *   // Anywhere in the app:
 *   import { getConfig } from './config/runtimeConfig';
 *   const { apiBaseUrl } = getConfig();
 */

export interface AppConfig {
  oktaIssuer: string;
  oktaClientId: string;
  oktaRedirectUri: string;
  apiBaseUrl: string;
  env: string;
}

let _config: AppConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  if (_config) {return _config;}

  const response = await fetch('/config/config.json');
  if (!response.ok) {
    throw new Error(`Failed to load runtime config: ${response.status} ${response.statusText}`);
  }

  const json = await response.json() as AppConfig;

  // Fall back to process.env REACT_APP_* values for local development
  // (config.json has empty strings in local — real values come from Terraform at deploy time)
  _config = {
    oktaIssuer:      json.oktaIssuer      || process.env.REACT_APP_OKTA_ISSUER       || '',
    oktaClientId:    json.oktaClientId    || process.env.REACT_APP_OKTA_CLIENT_ID    || '',
    oktaRedirectUri: json.oktaRedirectUri || process.env.REACT_APP_OKTA_REDIRECT_URI || '',
    apiBaseUrl:      json.apiBaseUrl      || process.env.REACT_APP_API_BASE_URL       || 'http://localhost:8080/api/v1',
    env:             json.env             || process.env.REACT_APP_ENV                || 'development',
  };

  return _config;
}

export function getConfig(): AppConfig {
  if (!_config) {
    throw new Error(
      'Runtime config not loaded. Ensure loadConfig() has resolved before the app renders.'
    );
  }
  return _config;
}
